/** @jsx jsx */
import { jsx } from '@emotion/react'
import React from 'react'
import { css } from '@emotion/react/macro'
import { gray } from '../../../../lib/styles/colors'

type ToggleButtonGroupProps = {
  /** Accessible label for the toggle group */
  ariaLabel: string
  /** Toggle buttons to render */
  children: React.ReactNode
  /** Change event for toggle */
  onChange: (newValue: string) => void
  /** Currently selected button */
  value: string
}

const groupCss = css`
  border: 1px solid ${gray};
  border-radius: 5px;
  display: inline-block;
  height: 36px;
  width: fit-content;
`

const isValueSelected = (value: string, candidate: string) => {
  if (candidate === undefined || value === undefined) {
    return false
  }

  if (Array.isArray(candidate)) {
    return candidate.indexOf(value) >= 0
  }

  return value === candidate
}

/** Adapted from Material UI Toggle Button Group
 * Currently only supports exclusive selection.
 */
const ToggleButtonGroup = ({ ariaLabel, children, onChange, value }: ToggleButtonGroupProps) => (
  <div css={groupCss} role="group" aria-label={ariaLabel}>
    {React.Children.map(children, (child) => {
      if (!React.isValidElement(child)) {
        return null
      }

      return React.cloneElement(child, {
        onClick: onChange,
        selected:
          child.props.selected === undefined
            ? isValueSelected(child.props.value, value)
            : child.props.selected
      })
    })}
  </div>
)

export default ToggleButtonGroup
